import React, { useEffect, useState } from 'react'
import { PhotographIcon, PaperClipIcon } from '@heroicons/react/outline'
import { XIcon } from '@heroicons/react/solid'
import { FileContainer, Label, MainFile } from './styles'
import { bytesToMB } from 'utils/bytesToMb'
import { MdOutlineAttachFile } from 'react-icons/md'
import { InputInfoLabelSubTitle } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/NotificationData/styles'
import { WarningModal } from 'components/Modals/WarningModal'
import { AiOutlineExclamationCircle } from 'react-icons/ai'
import { AttachmentProgressBar } from 'components/AttachmentProgressBar'

interface DropFileProps {
    onFileSelect: (file: File | null) => void
    setErrorFlag: (flag: boolean) => void
    errorFlag: boolean
    isMobile: boolean
    progressValue: number
    setProgressValue?: React.Dispatch<React.SetStateAction<number>>
}

const MBLIMIT = 6000000

export const DropFile: React.FC<DropFileProps> = ({ onFileSelect, errorFlag = false, setErrorFlag, isMobile, progressValue, setProgressValue }) => {
    const [file, setFile] = useState<File | null>(null)
    const [invalid, setInvalid] = useState(false)
    const [warningModal, setWarningModal] = useState(false)

    const fileTypeValidation = (file: File) => {
        const ext = file.name.split(".")[file.name.split(".").length - 1]
        return ext == "pdf" || ext == "xls" || ext == "xlsx" || ext == "csv"
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        handleFileAction(e.dataTransfer.files[0])
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileAction(e.target.files?.[0])
    }

    const handleFileAction = (file: File) => {
        if (fileTypeValidation(file) && file.size <= MBLIMIT) {
            setFile(file)
            onFileSelect(file)
            setErrorFlag(false)
        } else {
            setInvalid(true)
        }
    }

    const handleModal = () => {
        setWarningModal(true)
    }

    const removeAttachment = () => {
        setWarningModal(false)
        setInvalid(false)
        onFileSelect(null)
        setFile(null)
        setProgressValue(0)
    }

    useEffect(() => {
        setTimeout(() => {
            setInvalid(false)
        }, 5000);
    }, [invalid])

    
    useEffect(() => {
        if(errorFlag){
            removeAttachment();
        }
    }, [errorFlag])

    const getUploadFileName = (filename:string) => {
        const onlyFilename = getFileNameWithoutExtension(filename)
        if(onlyFilename.length > 18) {
            const fileExtension = filename.split('.').pop().toLowerCase();
            return onlyFilename.substring(0,18).concat('...').concat(fileExtension)
        } 
        
        return filename
    }

    const getFileNameWithoutExtension = (filename: string) => {
        const parts = filename?.split('.') || [];
        parts.pop();
        return parts.join('.');
    };


    const handleClick = () => {        
        const downloadFile = () => {
            if (file) {
                const fileURL = URL.createObjectURL(file); 
                const link = document.createElement('a'); 
                link.href = fileURL;
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link); 
                URL.revokeObjectURL(fileURL);
            }
        }

        downloadFile();
    };

    return (
        <div>
            <WarningModal
                icon={<AiOutlineExclamationCircle size={80} className="text-yellow" />}
                customStyle={{ titleStyle: 'text-yellow text-xl', subtitleStyle: 'font-normal' }}
                title="Remover anexo"
                isOpen={warningModal as boolean}
                subTitle="Tem certeza que deseja remover o documento anexo da notificação?"
                leftButtonText="Remover anexo"
                leftButtonAction={() => removeAttachment()}
                handleCloseModal={() => removeAttachment()}
                rightButtonText="Manter anexo"
                rightButtonAction={() => setWarningModal(false)}
            />
            <div onDragOver={handleDragOver} onDrop={handleDrop} id={`dropfile`} data-testid="dropfile">
                {!file && (
                    <>
                        <Label htmlFor="fileInput" className={`${errorFlag || invalid ? 'border-errorText' : 'border-blueTheme'}`}>
                            <MainFile>
                                {
                                    !isMobile && invalid &&
                                    <p className="font-bold text-sm text-errorText mr-1">
                                        Desculpe, formato ou tamanho do arquivo
                                        incompatível. Por favor, Tente novamente.
                                    </p>
                                }
                                <MdOutlineAttachFile className="w-[18px] h-[18px] text-blueTheme" />
                                <p className="ml-1 font-medium h-[18px] text-sm">
                                    <span className="text-darkGray">
                                    { !isMobile ?  'Arraste e solte ou' : 'Adicionar anexo' }
                                        </span>
                                    {' '}
                                    <span className="text-blueTheme underline">
                                        clique para procurar
                                    </span>
                                </p>
                            </MainFile>
                            <input
                                id="fileInput"
                                type="file"
                                className="hidden"
                                accept=".pdf,.xls,.xlsx,.csv"
                                onChange={handleFileSelect}
                            />
                        </Label>
                        {
                            isMobile && invalid &&
                            <p className="font-bold text-sm text-errorText mr-1 mt-2">
                                Desculpe, formato ou tamanho do arquivo
                                incompatível. Por favor, Tente novamente.
                            </p>
                        }
                        <InputInfoLabelSubTitle className='mt-2'>Permitido apenas 1 arquivo nos formatos <span className='font-semibold'>pdf, xls, xlsx e csv</span>. Tamanho máximo de 6 MB.</InputInfoLabelSubTitle>
                    </>
                )}
            </div>

            {file && (
                <div className="flex">
                    <FileContainer>
                        <div className='flex flex-row'>
                            <span className="flex items-center gap-1">
                                <PaperClipIcon className="w-4 h-4 text-blueTheme -rotate-45" />
                                <p  onClick={handleClick} className={`${isMobile ? 'font-medium text-xs' : 'font-medium'} text-darkGray`}>
                                    { isMobile? getUploadFileName(file.name) : file.name}
                                </p>
                            </span>

                            <span className={`${isMobile ? 'ml-2' : 'ml-4'} font-base text-[#47474799]`}>
                                {bytesToMB(file.size)}
                            </span>
                        </div>
                        <XIcon
                            data-testid="test-remove-file"
                            onClick={handleModal}
                            className="w-4 h-4 text-blueTheme cursor-pointer"
                        />
                    </FileContainer>
                </div>
            )}
            <AttachmentProgressBar progressBarValue={progressValue} />           

        </div>
    )
}
